import styled, { css, keyframes } from 'styled-components';
import { Link } from 'react-router-dom';

import { TransientProps } from '@common/types';

export const Container = styled.div`
  padding: 0.75rem 1rem 0.25rem;
`;

export const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.875rem;
`;

export const HeaderLeft = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: 0.375rem;
    color: ${theme.colors.brand.primary.default};

    > h3 {
      font-size: 0.875rem;
      font-weight: 700;
      color: ${theme.colors.foreground[100]};
    }
  `}
`;

export const FullAgendaLink = styled(Link)`
  ${({ theme }) => css`
    font-size: 0.6875rem;
    color: ${theme.colors.brand.primary.default};
    font-weight: 600;
  `}
`;

export const DaySelector = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.625rem;
  border-radius: 0.625rem;
  padding: 0.1875rem;
`;

type DayButtonProps = TransientProps<{ active: boolean }>;

export const DayButton = styled.button<DayButtonProps>`
  ${({ theme, $active }) => css`
    min-width: 2.75rem;
    padding: 0.375rem 0.5rem;
    border-radius: 0.375rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    background: ${$active ? theme.colors.brand.primary.default : 'transparent'};
    border: 1px solid
      ${$active
        ? theme.colors.brand.primary.default
        : theme.colors.background[40]};
    color: ${$active ? 'white' : theme.colors.foreground[60]};
    text-align: center;
    cursor: pointer;
    font-family: inherit;
    outline: none;
    transition: all 0.2s ease;

    &:hover {
      color: ${$active ? 'white' : theme.colors.brand.primary.default};
    }
  `}
`;

export const DayLabel = styled.div`
  font-size: 0.625rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  opacity: 0.8;
`;

export const DayNumber = styled.div`
  font-size: 1.05rem;
  font-weight: 700;
  margin-top: 0.0625rem;
  line-height: 1;
`;

export const TimelineItem = styled.div`
  display: flex;
  gap: 0.625rem;
  padding: 0.4375rem 0.5rem;
  border-radius: 0.625rem;
  margin-bottom: 0.125rem;
  transition: all 0.2s ease;
  cursor: pointer;

  &:hover {
    background: var(--colors-brand-primary-opaque);
  }
`;

type TimeLabelProps = TransientProps<{ active: boolean }>;

export const TimeLabel = styled.span<TimeLabelProps>`
  ${({ theme, $active }) => css`
    font-size: 0.75rem;
    font-weight: 700;
    color: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.foreground[60]};
    flex-shrink: 0;
  `}
`;

export const TitleSeparator = styled.span`
  ${({ theme }) => css`
    display: inline-block;
    width: 1.2px;
    height: 0.75rem;
    border-radius: 1px;
    background: ${theme.colors.foreground[40]};
    opacity: 0.6;
    flex-shrink: 0;
  `}
`;

export const DotColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 0.875rem;
  flex-shrink: 0;
`;

type DotProps = TransientProps<{ active: boolean }>;

const pulseAnim = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
`;

export const Dot = styled.div<DotProps>`
  ${({ theme, $active }) => css`
    width: ${$active ? '0.65rem' : '0.5rem'};
    height: ${$active ? '0.65rem' : '0.5rem'};
    border-radius: 50%;
    background: ${$active
      ? theme.colors.brand.primary.default
      : theme.colors.background[40]};
    ${$active &&
    css`
      border: 3px solid ${theme.colors.brand.primary.opaque};
    `}
    margin-top: 0.25rem;
    position: relative;
    z-index: 1;
  `}
`;

export const PulseRing = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: -5px;
    left: -5px;
    right: -5px;
    bottom: -5px;
    border-radius: 50%;
    border: 2px solid ${theme.colors.brand.primary.default};
    opacity: 0.4;
    animation: ${pulseAnim} 2s infinite;
  `}
`;

type LineProps = TransientProps<{ active: boolean }>;

export const Line = styled.div<LineProps>`
  ${({ theme, $active }) => css`
    width: 2px;
    flex: 1;
    margin-top: 0.25rem;
    margin-bottom: -0.375rem;
    border-radius: 1px;
    background: ${$active
      ? `linear-gradient(to bottom, ${theme.colors.brand.primary.default}, ${theme.colors.background[40]})`
      : theme.colors.background[60]};
  `}
`;

export const ContentColumn = styled.div`
  flex: 1;
  min-width: 0;
`;

export const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.1875rem;
  min-width: 0;
`;

export const SessionTitle = styled.span`
  font-size: 0.785rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
`;

export const LiveChip = styled.span`
  ${({ theme }) => css`
    font-size: 0.6rem;
    font-weight: 700;
    background: ${theme.colors.error[40]};
    color: ${theme.colors.error[100]};
    padding: 0.125rem 0.4375rem;
    border-radius: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  `}
`;

export const RoomBadge = styled.div`
  ${({ theme }) => css`
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 0.25rem;
    font-size: 0.625rem;
    font-weight: 600;
    padding: 0.125rem 0.4375rem;
    border-radius: 0.25rem;
    background: ${theme.colors.background[60]};
    color: ${theme.colors.foreground[60]};
    margin-top: 0.25rem;
  `}
`;

export const SpeakerBubbles = styled.div`
  display: flex;
  align-items: center;
  margin-top: 0.375rem;
`;

export const EmptyState = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    min-height: 18rem;
    padding: 2.5rem 0.75rem;
    gap: 0.5rem;

    > svg {
      color: ${theme.colors.brand.primary.default};
      opacity: 0.85;
      margin-bottom: 0.125rem;
    }

    > strong {
      font-size: 0.8125rem;
      font-weight: 600;
      color: ${theme.colors.foreground[100]};
    }

    > span {
      font-size: 0.6875rem;
      color: ${theme.colors.foreground[60]};
      line-height: 1.4;
      max-width: 14rem;
    }
  `}
`;

export const SpeakerBubble = styled.div<{ $index: number }>`
  ${({ theme, $index }) => css`
    width: 1.975rem;
    height: 1.975rem;
    border-radius: 0.5rem;
    overflow: hidden;
    border: 2px solid ${theme.colors.background[100]};
    margin-left: ${$index > 0 ? '-0.375rem' : '0'};
    z-index: ${3 - $index};

    > img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  `}
`;
